// Brand mapping and ownership, on one screen.
//
// Three decisions that are really one: which producer is ours, which of its brands are
// ours, and what each maps to in the actual-spend data. They were split across two tabs and
// that was wrong — you only ever have actual spend for your own brands, so deciding
// ownership somewhere else is how the two end up disagreeing.
//
// Ownership is not the same thing as mapping and both are kept: mapping says "Nivea and
// NIVEA MEA are the same advertiser", ownership says "Nivea is us". Ownership decides where
// a factor's numerator comes from; mapping decides what matches what.
//
// Unlike channels this is not a grouping problem. A channel group exists because one
// platform can report several Statex channels as a single figure; two brands are two
// advertisers, and collapsing them would be a data error rather than a modelling choice.
// So the mapping interaction is a single select per brand rather than a chip pool.

import { useState, useEffect, useCallback } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { T } from '../../../../shared/styles/tokens.js';

export default function BrandMappingSection() {
  const { clientId } = useClient();
  const [producer, setProducer] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    cmApi.getBrandPairs(clientId, producer || undefined)
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, producer]);

  useEffect(load, [load]);

  const assign = async (statexBrand, actualBrand) => {
    setBusy(statexBrand); setErr(null);
    try {
      await cmApi.saveBrandPair(clientId, {
        statex_brand: statexBrand,
        actual_brand: actualBrand || null,
      });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const setOwned = async (row, isOwn) => {
    setBusy(row.value); setErr(null);
    try {
      const res = await cmApi.setBrandOwnership(clientId, {
        brand_name: row.value, producer_name: row.producer, is_own: isOwn,
      });
      // Unticking a brand that a producer-wide rule covers leaves it owned. Say so rather
      // than letting the tick spring back with no explanation.
      if (!isOwn && res.is_own && res.via === 'producer') {
        setErr(`${row.value} is still ours: the producer rule for ${row.producer} covers `
             + `every brand under it. Untick the producer to release them all.`);
      }
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  const setProducerOwned = async (isOwn) => {
    setBusy('__producer'); setErr(null);
    try {
      await cmApi.setBrandOwnership(clientId, { producer_name: producer, is_own: isOwn });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };

  if (!clientId) return <p className="o-state">Select a client to map brands.</p>;

  const producers = data?.producers || [];
  const statex = data?.statex || [];
  const actuals = data?.actuals || [];
  const summary = data?.summary || {};
  const mapped = statex.filter(s => s.matched_actual).length;
  const ownCount = statex.filter(s => s.owned_via).length;
  const producerOwned = producer && statex.length > 0
    && statex.every(s => s.owned_via === 'producer' || s.owned_via === 'brand')
    && statex.some(s => s.owned_via === 'producer');

  // An actuals brand already taken by another Statex brand. Selecting it moves it, since
  // the relationship is one to one.
  const takenBy = (actualValue, exceptStatex) => {
    const owner = statex.find(s => s.matched_actual?.value === actualValue
                                   && s.value !== exceptStatex);
    return owner?.value;
  };

  return (
    <div>
      <p className="o-note">
        Pick your producer, mark which of its brands are yours, then map each to the name
        the actual-spend data uses. Ownership decides which rows a calibration factor is
        derived from; mapping decides what matches what. A brand spelled differently on each
        side never matches, so any factor grouped by brand produces nothing for it. Mapping
        is one to one — two brands are two advertisers, and merging them would be a data
        error rather than a choice.
      </p>

      {err && <div className="o-state o-error" style={{ textAlign: 'left', padding: `${T.s2}px 0` }}>{err}</div>}

      <div className="o-stats">
        <div className="o-stat">
          <b>{(summary.own_rows || 0).toLocaleString()}</b>
          <span>Our rows · {summary.own_brands || 0} brand{summary.own_brands === 1 ? '' : 's'}</span>
        </div>
        <div className="o-stat">
          <b>{(summary.competitor_rows || 0).toLocaleString()}</b>
          <span>Competitor rows · {summary.competitor_brands || 0} brand{summary.competitor_brands === 1 ? '' : 's'}</span>
        </div>
      </div>

      <div className="o-ctrl-row" style={{ padding: 0, border: 0, marginBottom: T.s4 }}>
        <div className="o-ctrl">
          <label className="o-field-lbl">Producer</label>
          <select className="o-control" style={{ minWidth: 240 }}
            value={producer} onChange={e => setProducer(e.target.value)}>
            <option value="">All producers ({statex.length} brands shown)</option>
            {producers.map(p => (
              <option key={p.value} value={p.value}>
                {p.value} — {p.brands} brand{p.brands === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </div>
        {producer && (
          <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
            <label className="o-field-lbl">Whole producer</label>
            {/* A producer-level rule names no brand, so it also covers brands that arrive
                in future imports — which ticking brands one at a time does not. */}
            <button className="o-btn" disabled={busy === '__producer'}
              onClick={() => setProducerOwned(!producerOwned)}>
              {producerOwned ? 'Release all its brands' : 'Mark all its brands as ours'}
            </button>
          </div>
        )}
        <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
          <p style={{ fontSize: T.tSmall, color: T.text3, margin: 0 }}>
            {ownCount} ours · {mapped} mapped · {statex.length} shown
          </p>
        </div>
      </div>

      {!producer && (
        <p className="o-hint">
          <b>Showing every advertiser in Statex.</b> Most belong to competitors, and only
          your own brands will ever have actual spend to map against. Pick your producer to
          narrow the list.
        </p>
      )}

      {producer && ownCount === 0 && (
        <p className="o-hint">
          <b>None of these brands are marked as yours.</b> A calibration factor is derived
          from your own spend, so nothing will be computed until at least one is.
        </p>
      )}

      {loading ? <p className="o-state">Loading…</p> : (
        <div className="o-panel" style={{ overflowX: 'auto' }}>
          <table className="o-tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Ours</th>
                <th>Statex brand</th>
                <th>Producer</th>
                <th>Rows</th>
                <th style={{ minWidth: 260 }}>Actual-spend brand</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {statex.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: T.text3 }}>
                  No Statex brands{producer ? ' for that producer' : ''}.
                </td></tr>
              )}
              {statex.map(s => {
                const current = s.matched_actual?.value || '';
                return (
                  <tr key={s.value} style={{ opacity: s.owned_via ? 1 : 0.7 }}>
                    <td>
                      <input type="checkbox" checked={!!s.owned_via}
                        disabled={busy === s.value}
                        onChange={e => setOwned(s, e.target.checked)}
                        aria-label={`${s.value} is our brand`}
                        title={s.owned_via === 'producer'
                          ? `Covered by the producer rule for ${s.producer}`
                          : undefined}
                        style={{ accentColor: T.ink, width: 15, height: 15, cursor: 'pointer' }} />
                    </td>
                    <td className="o-strong">{s.value}</td>
                    <td style={{ color: T.text3 }}>
                      {s.producer || '—'}
                      {s.owned_via === 'producer' && (
                        <span className="o-badge o-grey" style={{ marginLeft: 6 }}>via producer</span>
                      )}
                    </td>
                    <td className="o-num">{Number(s.rows).toLocaleString()}</td>
                    <td>
                      <select className="o-control" value={current}
                        disabled={busy === s.value}
                        onChange={e => assign(s.value, e.target.value)}>
                        <option value="">Not mapped</option>
                        {actuals.map(a => {
                          const owner = takenBy(a.value, s.value);
                          return (
                            <option key={`${a.source}|${a.value}`} value={a.value}>
                              {a.value}{owner ? ` — currently ${owner}` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td className="o-acts">
                      {s.matched_actual
                        ? <span className="o-badge o-ok">mapped</span>
                        : <span className="o-badge o-grey">unmapped</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="o-eyebrow" style={{ margin: `${T.s5}px 0 ${T.s2}px` }}>
        Actual-spend brands · {actuals.length}
      </p>
      <div className="o-chipset">
        {actuals.length === 0
          ? <span style={{ fontSize: T.tSmall, color: T.text3 }}>
              No actual-spend data imported yet.
            </span>
          : actuals.map(a => {
              const owner = statex.find(s => s.matched_actual?.value === a.value);
              return (
                <span key={`${a.source}|${a.value}`}
                      className={`o-badge ${owner ? 'o-ok' : 'o-grey'}`}>
                  {a.value} · {Number(a.rows).toLocaleString()}
                  {owner ? ` → ${owner.value}` : ''}
                </span>
              );
            })}
      </div>
      <p className="o-note" style={{ marginTop: T.s2 }}>
        An unmapped brand still matches an identical spelling on the other side; it just will
        not match a differently worded equivalent.
      </p>
    </div>
  );
}
